import { Spectra } from "../typings";

/**
 * A spectrum handed to MaCo in the link itself, instead of stored here.
 *
 * mabriadmin keeps the lab's peak lists in its own bucket, and MaCo only ever
 * needs the masses: `getMacoMatches` takes them as an argument. So the peak
 * list travels in the URL fragment — which a browser never sends to a server —
 * and nothing is uploaded, no order is created, no credit is spent.
 *
 * The format is the contract with mabriadmin (client/src/lib/maldi/maco-link.ts
 * there): `v1.` + base64url of
 *
 *   { label, database, databaseUsed?, peaks: [[mz, int], …],
 *     calibrated?: [[mz, int], …], shift?, profiles?: string[] }
 *
 * `databaseUsed`, `calibrated`, `shift` and `profiles` come from the internal analysis
 * endpoints and are absent when the classifier found nothing to calibrate on.
 */
type MacoLinkPayload = {
  label?: unknown;
  database?: unknown;
  databaseUsed?: unknown;
  peaks?: unknown;
  calibrated?: unknown;
  shift?: unknown;
  profiles?: unknown;
};

const PREFIX = "v1.";

const isPeakList = (value: unknown): value is [number, number][] =>
  Array.isArray(value) && value.every((peak) =>
    Array.isArray(peak) && peak.length === 2
    && typeof peak[0] === "number" && typeof peak[1] === "number");

const fromBase64Url = (text: string): string => {
  const base64 = text.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(base64 + "=".repeat((4 - (base64.length % 4)) % 4));
  // UTF-8 bytes back to text — the label carries "·". decodeURIComponent
  // rather than TextDecoder, which the test environment does not have.
  return decodeURIComponent(Array.from(binary,
    (char) => "%" + char.charCodeAt(0).toString(16).padStart(2, "0")).join(""));
};

/**
 * The spectrum in a link fragment, shaped like a stored one so the MaCo pages
 * can treat both alike — or an error that says what is wrong with the link.
 *
 * @param hash `location.hash`, with or without the leading `#`.
 */
export const decodeMacoLink = (hash: string): { spectra: Spectra } | { error: string } => {
  const encoded = hash.replace(/^#/, "");
  if (!encoded) return { error: "This link carries no spectrum." };
  if (!encoded.startsWith(PREFIX)) return { error: "This link is from a version MaCo does not know." };

  let payload: MacoLinkPayload;
  try {
    payload = JSON.parse(fromBase64Url(encoded.slice(PREFIX.length)));
  } catch {
    return { error: "The spectrum in this link could not be read." };
  }

  if (!isPeakList(payload.peaks) || payload.peaks.length === 0) {
    return { error: "The link carries no peaks." };
  }

  const label = typeof payload.label === "string" ? payload.label : "Spectrum from mabriadmin";
  const calibrated = isPeakList(payload.calibrated) && payload.calibrated.length > 0
    ? payload.calibrated.map(([mz, intensity]) => ({ mz, intensity }))
    : false;
  const profiles = Array.isArray(payload.profiles)
    && payload.profiles.every((entry) => typeof entry === "string")
    ? payload.profiles as string[]
    : false;
  const database = typeof payload.database === "string" ? payload.database : "default";

  return {
    spectra: {
      id: "",
      orderId: "",
      userId: "",
      result: false,
      nonQualifiedResult: false,
      matchedProfiles: profiles,
      masses: payload.peaks.map(([mz, int]) => ({ mz, int })),
      calibratedMasses: calibrated,
      database,
      // Which database the finding came from, as for a stored spectrum — MaCo
      // opens on it. Without it, the general one for the organism type.
      databaseUsed: typeof payload.databaseUsed === "string" ? payload.databaseUsed : database,
      sample: label,
      file: label,
      uploadedAt: undefined as never,
      error: false,
      quality: false,
      shift: typeof payload.shift === "number" ? payload.shift : 0,
    },
  };
};
